import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
} from '@shigo-ui/dashboard-ui';
import { Button, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { ShigoPermission } from '../../../../auth/permissions';
import { useNetworkFeatures } from '../../../../auth/useNetworkFeatures';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { usePlacementMutations } from '../../hooks';
import {
  isPlacementSortProperty,
  placementPageSizes,
  PlacementSortDirection,
  PlacementStatus,
  PlacementType,
  resolvePlacementStatusLabel,
  type Placement,
  type PlacementsFilter,
} from '../../model';
import { PlacementIntegrationsDialog } from '../../../../demand/integrations/components/PlacementIntegrationsDialog';
import { PlacementTargetingDialog } from '../PlacementTargetingDialog';
import { UnitTagPreviewDialog } from '../UnitTagPreviewDialog';
import { CreatePlacementAction } from './CreatePlacementAction';
import { buildPlacementColumns } from './placementsTableColumns';

export interface PlacementsDataTableProps {
  title?: string | undefined;
  tableLabel?: string | undefined;
  items: Placement[];
  totalItems: number;
  filter: PlacementsFilter;
  loading: boolean;
  fetching: boolean;
  error: boolean;
  statusCounts?: Partial<Record<PlacementStatus, number>> | undefined;
  showCreateAction?: boolean | undefined;
  publisherScoped?: boolean | undefined;
  onSearch: (value: string) => void;
  onStatusesChange: (value: PlacementStatus[]) => void;
  onTypesChange: (value: PlacementType[]) => void;
  onPaginationChange: (page: number, size: number) => void;
  onSortChange: (
    property: PlacementsFilter['sortProperty'],
    direction: PlacementSortDirection,
  ) => void;
  onRefresh: () => void;
}

export function PlacementsDataTable(props: PlacementsDataTableProps) {
  const { can } = useShigoPermissions();
  const { isFeatureAllowed } = useNetworkFeatures();
  const adUnitAllowed = isFeatureAllowed(NetworkAvailableFeature.AdUnit);
  const canCreate = can(
    props.publisherScoped
      ? ShigoPermission.SupplyPublishersPlacementsCreate
      : ShigoPermission.SupplyPlacementsCreate,
  );
  const canEdit =
    can(
      props.publisherScoped
        ? ShigoPermission.SupplyPublishersPlacementsEdit
        : ShigoPermission.SupplyPlacementsUpdate,
    ) ||
    (!props.publisherScoped && can(ShigoPermission.SupplyPlacementsEdit));
  const canArchive = can(
    props.publisherScoped
      ? ShigoPermission.SupplyPublishersPlacementsArchive
      : ShigoPermission.SupplyPlacementsArchive,
  );
  const canOpenEndpointGenerator = can(ShigoPermission.SupplyEndpointGeneratorRead);
  const mutations = usePlacementMutations();
  const search = useDebouncedValue(props.filter.searchTerm, props.onSearch, 650);
  const [statusPendingIds, setStatusPendingIds] = React.useState<Set<string>>(() => new Set());
  const [integrationsPlacement, setIntegrationsPlacement] = React.useState<Placement | null>(null);
  const [unitTagPlacement, setUnitTagPlacement] = React.useState<Placement | null>(null);
  const [targetingPlacement, setTargetingPlacement] = React.useState<Placement | null>(null);
  const columns = buildPlacementColumns({
    adUnitAllowed,
    canEdit,
    canArchive,
    canOpenEndpointGenerator,
    statusPendingIds,
    onArchive: (placement) => void changeArchived(placement),
    onShowIntegrations: setIntegrationsPlacement,
    onShowTargeting: setTargetingPlacement,
    onShowUnitTag: setUnitTagPlacement,
  });
  const statusLabel = props.filter.statuses.length
    ? props.filter.statuses.map(resolvePlacementStatusLabel).join(', ')
    : dashboardT('placements.allStatuses');
  const typeLabel = (type: PlacementType) =>
    dashboardT(
      type === PlacementType.Standard ? 'placements.type.standard' : 'placements.type.adUnit',
    );
  const selectedTypesLabel = props.filter.types.length
    ? props.filter.types.map(typeLabel).join(', ')
    : dashboardT('placements.allTypes');

  async function changeArchived(placement: Placement) {
    setStatusPendingIds((current) => new Set(current).add(placement.id));
    try {
      await mutations.setArchived.mutateAsync({
        id: placement.id,
        archived: placement.status !== PlacementStatus.Archived,
      });
      toast.success(dashboardT('placements.statusChanged'));
    } catch (error) {
      showRequestError(error, dashboardT('placements.statusError'));
    } finally {
      setStatusPendingIds((current) => {
        const next = new Set(current);
        next.delete(placement.id);
        return next;
      });
    }
  }

  return (
    <>
      <DashboardDataTable
        title={props.title}
        tableLabel={props.tableLabel ?? props.title}
        columns={columns}
        items={props.items}
        getRowKey={(placement) => placement.id}
        loading={props.loading}
        loadingMessage={dashboardT('placements.loading')}
        errorMessage={props.error ? dashboardT('placements.loadError') : undefined}
        emptyMessage={dashboardT('placements.empty')}
        minWidthClassName={adUnitAllowed ? 'min-w-[1640px]' : 'min-w-[1440px]'}
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('placements.search')}
              placeholder={dashboardT('placements.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DashboardTableMultiSelectFilter
              ariaLabel={dashboardT('placements.statusFilter')}
              label={statusLabel}
              value={props.filter.statuses}
              options={Object.values(PlacementStatus).map((status) => ({
                value: status,
                label: resolvePlacementStatusLabel(status),
                count: props.statusCounts?.[status],
              }))}
              onChange={props.onStatusesChange}
            />
            {adUnitAllowed ? (
              <DashboardTableMultiSelectFilter
                ariaLabel={dashboardT('placements.typeFilter')}
                label={selectedTypesLabel}
                value={props.filter.types}
                options={Object.values(PlacementType).map((type) => ({
                  value: type,
                  label: typeLabel(type),
                }))}
                onChange={props.onTypesChange}
              />
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={dashboardT('placements.refresh')}
              disabled={props.fetching}
              onClick={props.onRefresh}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </Button>
            {props.showCreateAction !== false && canCreate ? (
              <CreatePlacementAction adUnitAllowed={adUnitAllowed} />
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: props.filter.page,
            pageSize: props.filter.size,
            totalItems: props.totalItems,
          },
          pageSizes: placementPageSizes,
          disabled: props.fetching,
          onChange: (next) => {
            props.onPaginationChange(next.page, next.pageSize);
          },
        }}
        sort={{
          value: props.filter.sortProperty
            ? {
                key: props.filter.sortProperty,
                direction:
                  props.filter.sortDirection === PlacementSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              props.onSortChange(null, props.filter.sortDirection);
              return;
            }
            if (!isPlacementSortProperty(next.key)) return;
            props.onSortChange(
              next.key,
              next.direction === DashboardDataTableSortDirection.Asc
                ? PlacementSortDirection.Asc
                : PlacementSortDirection.Desc,
            );
          },
        }}
      />
      {integrationsPlacement ? (
        <PlacementIntegrationsDialog
          placement={integrationsPlacement}
          onOpenChange={(open) => {
            if (!open) setIntegrationsPlacement(null);
          }}
        />
      ) : null}
      <UnitTagPreviewDialog
        placement={unitTagPlacement}
        onOpenChange={(open) => {
          if (!open) setUnitTagPlacement(null);
        }}
      />
      {targetingPlacement ? (
        <PlacementTargetingDialog
          placement={targetingPlacement}
          onOpenChange={(open) => {
            if (!open) setTargetingPlacement(null);
          }}
        />
      ) : null}
    </>
  );
}
